import { HttpClient, HttpParams } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { lastValueFrom, Observable, of } from "rxjs";
import { map, take } from "rxjs/operators";

import { EnvService } from "../../env.service";
import { getbaseUrl } from "../helpers/util";
import { SetSettingsCommand } from "../model/interfaces";
import { ProfileData, User, UserAction, UserType } from "../model/model";
import { LocalstorageService } from "./localstorage-service.service";

@Injectable()
export class UserService {
  public user: User | null = null;
  public userRetrieved = new EventEmitter();
  public queryEndPoint = "query-api/User";
  public queryEndPointHelp = "query-api/Help";
  public queryEndPointGroup = "query-api/Group";
  public commandEndPoint = "command-api/User";
  // constructor(private http: HttpClient, private router: Router) { }
  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelperService: JwtHelperService,
    private env: EnvService,
    private storageService: LocalstorageService,
  ) {}

  getCurrentUser = (): Observable<User> =>
    this.user
      ? of(this.user)
      : this.http.post<User>(`authenticate/Login/loggedinuser`, {}).pipe(
          map((u) => {
            this.user = u;
            this.userRetrieved.emit(u);
            return u;
          }),
        );

  isCoordinator = (): Observable<boolean> =>
    this.getCurrentUser().pipe(
      map((u) => {
        const isCoordinator =
          !!u &&
          (u.userType === UserType.coordinator ||
            u.userType === UserType.system);
        return isCoordinator;
      }),
    );

  showReleaseNotes = (): Observable<any> => {
    return this.http.get(`${this.queryEndPointHelp}/ReleaseNotes`);
  };

  showOnboarding = (): Observable<any> => {
    return this.http.get(`${this.queryEndPointHelp}/Onboarding`);
  };

  releasenotesRead = (): Observable<any> => {
    return this.http.get(`${this.commandEndPoint}/DontShowCurrentReleaseNotes`);
  };

  onboardingRead = (dontshowagain: boolean): Observable<any> => {
    let body = new HttpParams();
    body = body.set("disabled", dontshowagain.toString());
    return this.http.get(`${this.commandEndPoint}/ChangeOnboarding`, {
      params: body,
    });
  };

  get = (groupId: string): Observable<User[]> => {
    return this.http.get<User[]>(
      `${this.queryEndPointGroup}/${groupId}/Candidates`,
    );
  };

  getCandidates = (
    queryArr = this.user?.groupList,
    previousObservable = null,
    counter = 0,
  ): Observable<User[]> => {
    if (queryArr && queryArr.length != counter) {
      const url = `${this.queryEndPointGroup}/${queryArr[counter]}/Candidates`;
      let observable = null;
      if (previousObservable) {
        const observable$ = this.http.get(url);
        observable = lastValueFrom(observable$);

        observable = Promise.all([observable, previousObservable])
          .then(function (values) {
            var merged = [].concat.apply([], values);
            return merged;
          })
          .catch((err) => console.log(err));
        counter++;
      } else {
        const observable$ = this.http.get(url);

        observable = lastValueFrom(observable$);
        counter++;
      }
      return this.getCandidates(queryArr, observable, counter);
    } else {
      return previousObservable;
    }
  };

  loginUser = (jwt: string) => {
    sessionStorage.setItem(this.env.jwt, jwt);
    this.getCurrentUser()
      .pipe(take(1))
      .subscribe(() => this.router.navigate(["/dashboard"]));
  };

  getSelectedList = () => {
    const list =
      this.user && this.user.settings
        ? this.user.settings.find((s) => s.key === "SELECTED_LIST")
        : null;
    return list ? of(list.value) : of(null);
  };
  setSelectedList = (value: string) => {
    const setting = { key: "SELECTED_LIST", value: value };
    return this.http.post(
      `${this.commandEndPoint}/StoreSettings`,
      <SetSettingsCommand>{ settings: [setting] },
      { responseType: "text" },
    );
  };

  updateName = (id: string, newname: string) =>
    this.http.post(
      `${this.commandEndPoint}/ChangeUsersName`,
      {
        candidateId: id,
        name: newname,
      },
      { responseType: "text" },
    );

  updateProfile = (id: string, profile: ProfileData) =>
    this.http.put(
      `${this.commandEndPoint}/LearningRoutes`,
      {
        userId: id,
        learningRoutes: profile.learningRoutes,
        learningRoutesSpelling: profile.learningRoutesSpelling,
      },
      { responseType: "text" },
    );

  logActions = (actions: UserAction[]) =>
    this.http.post(
      `${this.commandEndPoint}/LogAction`,
      { actions: actions },
      { responseType: "text" },
    );

  logout = () => {
    // Remove token and profile from sessionStorage
    sessionStorage.removeItem(this.env.jwt);

    this.user = null;
    window.location.href = `${getbaseUrl()}/login`;
  };

  isTokenAlmostExpired = async () => {
    if (!sessionStorage.getItem(this.env.jwt)) return false;

    var almostExpired = false;
    var checkForMinutesBefore = 5;

    var exp = await this.jwtHelperService.getTokenExpirationDate();

    let expTime = exp ? exp.getTime() : 0;

    var now = new Date().getTime();

    var diffMs = expTime - now; // milliseconds between
    var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes

    if (diffMins <= checkForMinutesBefore) {
      almostExpired = true;
    }
    return almostExpired;
  };

  isTokenExpired = async (): Promise<boolean> => {
    if (!sessionStorage.getItem(this.env.jwt)) return false;

    var isExpired = await this.jwtHelperService.isTokenExpired();
    if (isExpired) {
      console.error("JWT Token is expired!");
    }

    return isExpired;
  };

  getNewToken(): Observable<any> {
    return this.http.post<any>(`authenticate/Token/refresh`, {}).pipe(
      map((result) => {
        if (result) {
          sessionStorage.setItem(this.env.jwt, result.jwt);
        }

        return <any>result;
      }),
    );
  }
}
